// DESIGN-SPEC §5.1 SpecimenLabel 溯源标签
import type { CSSProperties, ReactNode } from "react";
import { cn } from "@/lib/cn";

type SpecimenLabelVariant =
  | "eyebrow"
  | "origin"
  | "batch"
  | "invite"
  | "inverse";
type SpecimenLabelTone = "default" | "tea" | "hirudin" | "herbal";

export interface SpecimenLabelProps {
  code: string;
  name: string;
  variant?: SpecimenLabelVariant;
  tone?: SpecimenLabelTone;
  className?: string;
  children?: ReactNode;
}

const toneClasses: Record<SpecimenLabelTone, string> = {
  default: "border-line-strong text-ink",
  tea: "border-tea text-tea",
  hirudin: "border-hirudin text-hirudin",
  herbal: "border-tea text-tea",
};

const invitePerforation: CSSProperties = {
  WebkitMaskImage:
    "radial-gradient(circle at 0 6px, transparent 0 3px, black 3.5px)",
  WebkitMaskPosition: "left top",
  WebkitMaskRepeat: "repeat-y",
  WebkitMaskSize: "100% 12px",
  maskImage:
    "radial-gradient(circle at 0 6px, transparent 0 3px, black 3.5px)",
  maskPosition: "left top",
  maskRepeat: "repeat-y",
  maskSize: "100% 12px",
};

export function SpecimenLabel({
  code,
  name,
  variant = "eyebrow",
  tone = "default",
  className,
  children,
}: SpecimenLabelProps) {
  const isInvite = variant === "invite";
  const isInverse = variant === "inverse";
  const colorClasses = isInverse
    ? "border-paper-inverse/55 text-paper-inverse"
    : toneClasses[tone];
  const codeColorClasses = isInverse
    ? "text-paper-inverse/70"
    : tone === "default"
      ? "text-ink-3"
      : tone === "tea" || tone === "herbal"
        ? "text-tea"
        : "text-hirudin";

  return (
    <div
      className={cn(
        "inline-block border p-[3px] text-left",
        variant === "origin" && "bg-forest-100",
        variant === "batch" && "bg-card",
        isInverse && "bg-black/10 backdrop-blur-[2px]",
        isInvite && "bg-card pl-2",
        colorClasses,
        className,
      )}
      style={isInvite ? invitePerforation : undefined}
    >
      <div
        className={cn(
          "flex border px-2.5 py-1.5",
          isInvite ? "items-center gap-4" : "flex-col",
          colorClasses,
        )}
      >
        <div className="min-w-0">
          <span
            className={cn(
              "flex items-center font-mono text-[11px] leading-4 font-medium tracking-[0.12em] uppercase",
              codeColorClasses,
              isInvite && "text-[20px] leading-7 tracking-[0.2em]",
            )}
          >
            {variant === "batch" && (
              <span
                aria-hidden="true"
                className="mr-1.5 size-1.5 shrink-0 rounded-full bg-success"
              />
            )}
            {code}
          </span>
          <span className="mt-0.5 block font-display text-[13px] leading-5 font-semibold text-current">
            {name}
          </span>
        </div>
        {isInvite && children ? (
          <div className="ml-auto shrink-0">{children}</div>
        ) : null}
      </div>
    </div>
  );
}
